import { FileDescriptor } from "./file-descriptor";

/**
 * A file that can be exchanged through API.
 */
export class ExchangeFile {


    public constructor(
        /**
         * The file name
         */
        public readonly name: string,
        /**
         * The content of the file as a data URL
         */
        public readonly contentUrl: string,
    ) {
    }

    /**
     * Instantiate a new ExchangeFile with value from a JSON or POJO.
     * 
     * @param json a plain old JS object that represents an exchange file
     * @returns the ExchangeFile described by the JSON or POJO ; or null if the json does not describe an ExchangeFile
     */
    public static fromJson(json: any): ExchangeFile {
        const name: string = json?.name;
        const contentUrl: string = json?.contentUrl;

        if (name && contentUrl) {
            return new ExchangeFile(name, contentUrl);
        } else {
            return null;
        }
    }

    /**
     * Create a new exchange file from a File
     * @param file the File we want to exchange
     * @returns an exchange file with the same content as file
     */
    public static async fromFile(file: File): Promise<ExchangeFile> {
        return new ExchangeFile(file.name, await ExchangeFile.blobToDataUrl(file))
    }

    /**
     * Create a File with the same content as an exchange file.
     * 
     * Needs 'data:' URL to ba added in CSP, which is not safe.
     * 
     * @returns A File with the content represented by the exchanged file
     */
    public async toFile(): Promise<File> {
        return fetch(this.contentUrl)
            .then(response => response.blob())
            .then(blob => new File([blob], this.name, {type: blob.type}));
    }

    /**
     * @returns A descriptor for the file
     */
    public toDescriptor(): FileDescriptor {
        const splitUrl = this.contentUrl.split(/[;,]/);
        const isBase64 = splitUrl[1] === 'base64';
        const content = splitUrl[splitUrl.length - 1];

        if (isBase64) {
            return new FileDescriptor(this.name, ExchangeFile.base64Length(content))
        } else { // Content is URI encoded
            return new FileDescriptor(this.name, ExchangeFile.uriLength(content))
        }
    }

    /**
     * Compute the length of a base64-represented content
     * @param content the base64 representation of the content
     * @returns the length of the content
     */
    private static base64Length(content: string) {
        if (!content) {
            return 0;
        }

        const contentSize = content.length;

        // Size of the content is 3/4 of the base64, with adjustment for the up to two last bytes last bytes
        let paddingCharCount = 0; // base64 content is a mulitple of 4, and there is no padding
        if (content.endsWith('==') || contentSize % 4 == 2) {
            paddingCharCount = 2; // last byte: 2 base64 chars + 2 padding chars (possibly not included)
        } else if (content.endsWith('=') || contentSize % 4 == 3) {
            paddingCharCount = 1; // two last bytes: 3 base base64 chars + 1 padding char (possibly not included)
        }
        // We round up in case the base64 is not padded
        return 3 * Math.ceil(contentSize / 4) - paddingCharCount;
    }

    /**
     * Compute the length of a URI-encoded content
     * @param content the URI-encoded representation of the content
     * @returns the length of the content
     */
    private static uriLength(content: string) {
        if (!content) {
            return 0;
        }

         // decodeURIComponent because we want to decode '/', ',', ... as well
        return decodeURIComponent(content).length;
    }

    /**
     * Create a data URL that represents a blob
     * @param blob the blob to write as a data URL
     * @returns A data URL that represents the blob
     */
    private static async blobToDataUrl(blob: Blob): Promise<string> {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
    
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = () => reject(reader.error);
          reader.onabort = () => reject(new Error("Reading the logo was aborted"));
    
          reader.readAsDataURL(blob);
        });
      }
}
